import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Orders_ServiceOrdersResponse'; // side-effect: register runtime validator
import '../contracts/Orders_PorClienteResponse'; // side-effect: register runtime validator
import type { Orders_ServiceOrderRow } from '../contracts/Orders_ServiceOrderRow';
import type { Orders_ServiceOrderItemRow } from '../contracts/Orders_ServiceOrderItemRow';
import type { Orders_PorClienteResponse } from '../contracts/Orders_PorClienteResponse';

/**
 * One table row = one service order (header only). Items are nested inside
 * the `items` array and accessible by clicking the order ID or a "Ver itens"
 * button — no item-level flattening. This avoids repeating the same OS header
 * for every item.
 */
export interface SOOrderFlatRow {
  so_order_id: number;
  so_order_franchise_id: number;
  so_order_status: string;
  so_order_financial_status: string;
  so_order_total: number;
  so_order_total_recebido: number;
  so_order_total_pendente: number;
  so_order_client_name?: string;
  franquia?: string;
  cliente_ddi?: string;
  cliente_ddd?: string;
  cliente_telefone?: string;
  cliente_email?: string;
  cliente_cpf?: string;
  cliente_document_type?: string;
  so_order_items_count: number;
  /** Nested items — each entry is a service-order item row shape */
  items?: Orders_ServiceOrderItemRow[];
}

export function useSOOrders() {
  const rows = ref<SOOrderFlatRow[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchServiceOrders = async () => {
    log('useSOOrders', 'fetchServiceOrders', 'Fetching service orders');
    isLoading.value = true;
    error.value = null;
    try {
      const res = await httpService.get(
        '/index.php/so/orders/service_orders',
        { responseContract: 'Orders_ServiceOrdersResponse' }
      );
      if (res.success) {
        // One row per OS (no flattening) — items stay nested.
        rows.value = (res.dados ?? []).map((h: Orders_ServiceOrderRow): SOOrderFlatRow => {
          const { items, ...header } = h;
          return { ...header, items: items ?? [] };
        });
        return rows.value;
      }
      throw new Error(res.error ?? 'Erro ao buscar ordens de serviço');
    } catch (err) {
      logError('useSOOrders', 'fetchServiceOrders', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchServiceOrdersByCliente = async (clienteId: number | string) => {
    log('useSOOrders', 'fetchServiceOrdersByCliente', 'Fetching service orders for client', clienteId);
    isLoading.value = true;
    error.value = null;
    try {
      const res = await httpService.get<Orders_PorClienteResponse>(
        `/index.php/so/orders/por_cliente/${clienteId}`,
        { responseContract: 'Orders_PorClienteResponse' }
      );
      if (res.success) {
        return res.dados ?? [];
      }
      throw new Error(res.error ?? 'Erro ao buscar ordens de serviço do cliente');
    } catch (err) {
      logError('useSOOrders', 'fetchServiceOrdersByCliente', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return { rows, isLoading, error, fetchServiceOrders, fetchServiceOrdersByCliente };
}
